import {
  Box,
  Text,
  ModalContent,
  Image,
  IconButton,
  ModalCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from "@chakra-ui/react";
import { DownloadIcon } from "@chakra-ui/icons";
import React, { useContext, useEffect, useState } from "react";
import { LoanContext } from "providers/loan";
import _ from "underscore";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { uploadStatement } from "utils/api";
import { numberToDollar, stringToFullNumber } from "utils/helpers";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useQueryClient } from "@tanstack/react-query";

interface LateStatementPopupContentProps {
  singleStatement: any;
  printref?: any;
  onClose: () => void;
}

const LateStatementPopupContent: React.FC<LateStatementPopupContentProps> = ({
  singleStatement,
  printref,
}) => {
  const queryClient = useQueryClient();
  const { loan } = useContext(LoanContext);
  const { user } = useTenant();
  const [budget, setBudget] = useState(0);
  const [items, setItems] = useState<any[]>([]);
  const [, setSections] = useState<{
    [key: number]: any[];
  }>({});
  const [, setLoading] = useState(true);

  const custom_currency =
    user?.company?.company_configurations?.[0]?.custom_currency?.currency || "$";

  const getLoanType = (loan: any) => {
    let loanObj =
      loan?.values?.find(
        (v: { loan_field: { label: string }; value: { field: { InterestRate?: number } } }) =>
          v?.loan_field?.label === "Interest Rate" && v?.value?.field !== null
      )?.value?.field?.LoanType ?? null;
    if (loanObj) {
      return `${loanObj}`;
    } else {
      return "Not Provided";
    }
  };

  const pullInterestRate = (loan: any) => {
    let loanObj =
      loan?.values?.find(
        (v: { loan_field: { label: string }; value: { field: { InterestRate?: number } } }) =>
          v?.loan_field?.label === "Interest Rate" && v?.value?.field !== null
      )?.value?.field?.InterestRate ?? null;
    if (loanObj) {
      return `${loanObj}%`;
    } else {
      return "Not Provided";
    }
  };

  useEffect(() => {
    const groupedSections = _.groupBy(items, item => item.section);
    setSections(groupedSections);
    const newBudget = items.reduce((prev, curr) => (prev += curr.amount), 0);
    setBudget(newBudget);
  }, [items]);

  useEffect(() => {
    const newBudget = items.reduce((prev, curr) => (prev += curr.amount), 0);
    setBudget(newBudget);
    setItems(loan!.draw_requests);
  }, []);

  const handlePrint = () => {
    const modalContentElement = printref.current;
    if (modalContentElement) {
      modalContentElement.classList.add("print-mode");
      window.print();
      modalContentElement.classList.remove("print-mode");
    }
  };

  const updateBase64PDFStatement = async () => {
    setLoading(true);
    const modalContentElement = printref.current;
    if (modalContentElement) {
      html2canvas(modalContentElement, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
      })
        .then(async canvas => {
          modalContentElement.classList.add("print-mode");
          const imgData = canvas.toDataURL("image/png");
          const pdf = new jsPDF();
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = pdf.internal.pageSize.getHeight();
          const imgWidth = canvas.width;
          const imgHeight = canvas.height;
          const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, 0, pdfWidth, pdfHeight, "F");
          pdf.addImage(imgData, "PNG", 0, 0, imgWidth * ratio, imgHeight * ratio);
          // pdf.save("download.pdf");
          // Move the await call inside the async function
          try {
            const generatedPDF = await pdf.output("blob");
            await uploadStatement({
              file: generatedPDF,
              currentStatementId: singleStatement.id,
            });
          } catch (error) {
            console.error("Error uploading PDF:", error);
          } finally {
            modalContentElement.classList.remove("print-mode");
            setLoading(false);
            queryClient.invalidateQueries({
              queryKey: ["statements", loan!.id],
            });
          }
        })
        .catch(error => {
          console.error("Error capturing canvas:", error);
        });
    } else {
      console.error("Element with id 'print-mode' not found.");
    }
  };

  useEffect(() => {
    if (singleStatement !== "") {
      setTimeout(() => {
        updateBase64PDFStatement();
      }, 3000);
    }
  }, [singleStatement]);

  return (
    <ModalContent
      minW="873px"
      height={"900px"}
      borderRadius={"15px"}
      ref={printref}
      className="print-mode add-an-width"
    >
      <Box display={"flex"} marginTop={"10px"} borderBottomWidth="1px" height={"50px"}>
        <Text color={"black"} marginLeft={"33px"}>
          Late Statements
        </Text>
        <IconButton
          className="hide-on-print"
          aria-label="Download"
          icon={<DownloadIcon />}
          marginLeft={"68%"}
          color="black"
          background="white"
          borderRadius="50%"
          _hover={{ color: "#ffffff", background: "black" }}
          onClick={() => handlePrint()}
        />
        <Box marginRight={"20px"} marginLeft={"10px"}>
          <ModalCloseButton marginTop={"5px"} fontSize={"15px"} className="hide-on-print" />
        </Box>
      </Box>

      <Box height={"20px"} marginTop={"20px"} marginLeft={"34px"}>
        <Image
          src={loan?.lender_company?.logo ?? ""}
          alt="company-logo"
          background={"E0E0E0"}
          width={"200px"}
        />
      </Box>

      <Box display={"flex"} marginTop={"20px"}>
        <Box marginLeft={"34px"} marginTop={"25px"}>
          <Text color={"black"}>{loan?.location?.formatted || "Not Provided"}</Text>
        </Box>

        <Box marginLeft={"56%"} marginRight={"20px"}>
          <Text color={"black"} marginLeft={"1px"}>
            Loan Number : {`#${loan?.id.split("-")[0]}`}
          </Text>
          <Text color={"black"} marginLeft={"10px"}>
            Total amount due date :{" "}
          </Text>
          <Text
            fontSize={"30px"}
            color={"black"}
            fontWeight={"600"}
            marginLeft={"35px"}
            marginRight={"14px"}
          >
            {/* {stringToDollar(loan.amount) || "Not Provided"} */}
            $300.00
          </Text>
        </Box>
      </Box>

      {/* For the current payment */}
      <Box marginLeft={"30px"} marginRight={"15px"}>
        <Box>
          <Text
            background="#5A5A5A"
            color={"#ffffff"}
            height={"40px"}
            marginTop={"5px"}
            paddingTop={"6px"}
            paddingLeft={"5px"}
            borderRadius={"5px"}
            marginRight={"20px"}
          >
            Current Payment
          </Text>
          <Box display={"flex"} marginTop={"15px"}>
            <Box width={"50%"}>
              <Box borderBottomWidth="1px" fontWeight={"650"}>
                Payment overview
              </Box>
              <Box display={"flex"} flexDirection={"column"} borderBottomWidth="1px">
                <Box display={"flex"} justifyContent="space-between">
                  <Text color={"black"} display={"flex"} my={0.8}>
                    Underwriting fee :
                  </Text>
                  <Text color={"black"} my={0.8}>
                    $250.00
                  </Text>
                </Box>
              </Box>

              <Box borderBottomWidth="1px">
                <Box display={"flex"} flexDirection={"column"}>
                  <Text fontWeight={"650"} color={"black"}>
                    Regularly Monthly payments
                  </Text>
                  <Box display={"flex"} justifyContent="space-between">
                    <Text display={"flex"} color={"black"} my={0.8}>
                      {" "}
                      Fees :
                    </Text>
                    <Text color={"black"} my={0.8}>
                      $0.00
                    </Text>
                  </Box>
                  <Box display={"flex"} justifyContent="space-between">
                    <Text display={"flex"} color={"black"} my={0.8}>
                      {" "}
                      Late fee :{" "}
                    </Text>
                    <Text color={"black"} my={0.8}>
                      $50.00
                    </Text>
                  </Box>
                </Box>
              </Box>

              <Box>
                <Box display={"flex"} justifyContent="space-between">
                  <Text color={"black"} fontWeight={"650"} my={0.8}>
                    Total amount due :
                  </Text>
                  <Text color={"black"} my={0.8}>
                    $300.00
                  </Text>
                </Box>
                <Box>
                  <span> Due on Oct 30, 2023</span>
                </Box>
              </Box>
            </Box>

            <Box width={"50%"} marginLeft={"35px"} marginRight={"20px"}>
              <Box borderBottomWidth="1px" fontWeight={"650"}>
                Account information
              </Box>
              <Box display={"flex"} justifyContent="space-between">
                <Text color={"black"} my={0.8}>
                  Statement date :
                </Text>
                {/* <Text color={'black'} my={0.8}>{format(new Date(singleStatement?.created_at), 'MMM d, yyyy')}</Text> */}
              </Box>
              <Box display={"flex"} justifyContent="space-between">
                <Text color={"black"} my={0.8}>
                  Loan status :{" "}
                </Text>
                <Text color={"black"}>{loan?.status?.label}</Text>
              </Box>
              <Box display={"flex"} justifyContent="space-between">
                <Text color={"black"} display={"flex"} my={0.8}>
                  {" "}
                  Remaining principal :{" "}
                </Text>
                <Text color={"black"} my={0.8}>
                  {custom_currency
                    ? numberToDollar(stringToFullNumber(loan!.amount) - budget, custom_currency)
                    : numberToDollar(stringToFullNumber(loan!.amount) - budget)}
                </Text>
              </Box>
              <Box display={"flex"} justifyContent="space-between">
                <Text color={"black"} display={"flex"} my={0.8}>
                  Interest Rate ({getLoanType(loan)}):
                </Text>
                <Text color={"black"} my={0.8}>
                  {pullInterestRate(loan)}
                </Text>
              </Box>
              <Box display={"flex"} justifyContent="space-between">
                <Text color={"black"} display={"flex"} my={0.8}>
                  Funding Date :
                </Text>
                <Text color={"black"} my={0.8}>
                  {singleStatement?.loans?.funding_date}
                </Text>
              </Box>
              <Box display={"flex"} justifyContent="space-between">
                <Text color={"black"} my={0.8}>
                  Principal paid YTD :{" "}
                </Text>
                <Text color={"black"} my={0.8}>
                  $0.00
                </Text>
              </Box>
              <Box display={"flex"} justifyContent="space-between">
                <Text color={"black"} my={0.8}>
                  Interest paid YTD :{" "}
                </Text>
                <Text color={"black"} my={0.8}>
                  $0.00
                </Text>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* For the Activity since statement */}
      <Box>
        <Box marginLeft={"30px"} marginRight={"35px"}>
          <Text
            background="#5A5A5A"
            color={"#ffffff"}
            height={"40px"}
            marginTop={"15px"}
            paddingTop={"6px"}
            paddingLeft={"5px"}
            borderRadius={"5px"}
          >
            Activity since last statement(Sep 15 - oct 15)
          </Text>
        </Box>
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        width="100%"
        flexDirection="row"
        gap="3"
        mx="auto"
      >
        <Box width="50%" ml="6" my="2">
          <TableContainer>
            <Table variant="simple" overflow="hidden">
              <Thead>
                <Tr borderEndStyle="hidden" borderBottomWidth="1px">
                  <Th color={"black"}>Date</Th>
                  <Th color={"black"}>Transaction</Th>
                  <Th color={"black"} isNumeric>
                    Charge
                  </Th>
                  <Th color={"black"} isNumeric>
                    Payment
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr borderBottom="none">
                  <Td>Oct 5</Td>
                  <Td>Processing fee</Td>
                  <Td isNumeric>$ 7,000.00</Td>
                  <Td isNumeric>$ 7,000.00</Td>
                </Tr>
              </Tbody>
            </Table>
          </TableContainer>
        </Box>

        <Box width={"47%"} mr="8" my="2">
          <Box borderBottomWidth="1px" fontWeight={"650"}>
            Past payments breakdown
          </Box>
          <Box display={"flex"} justifyContent="space-between">
            <Text color={"black"} my={0.8}>
              Principal :
            </Text>
            <Text color={"black"} my={0.8}>
              $0.00
            </Text>
          </Box>
          <Box display={"flex"} justifyContent="space-between">
            <Text color={"black"} my={0.8}>
              Interest :{" "}
            </Text>
            <Text color={"black"}>$0.00</Text>
          </Box>
          <Box display={"flex"} justifyContent="space-between" borderBottomWidth="1px">
            <Text color={"black"} display={"flex"} my={0.8}>
              Fees :
            </Text>
            <Text color={"black"} my={0.8}>
              $0.00
            </Text>
          </Box>
          <Box display={"flex"} justifyContent="space-between">
            <Text color={"black"} fontWeight={"650"} display={"flex"}>
              Total :
            </Text>
            <Text color={"black"} fontWeight={"650"}>
              $0.00
            </Text>
          </Box>
        </Box>
      </Box>
    </ModalContent>
  );
};

export default LateStatementPopupContent;
